import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { useAuth } from "../../contexts/AuthContext";
import { Plus, ChevronRight, MapPin, CalendarDays } from "lucide-react";

const fmtPrice = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (s) => {
  if (!s) return "-";
  try { return new Date(s).toLocaleDateString("fr-FR"); } catch { return "-"; }
};

export default function ClientOrders() {
  const { privacy } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="client-orders-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="lc-small-label mb-1">Suivi</div>
          <h1 className="display text-3xl sm:text-4xl font-extrabold tracking-tight">Mes commandes</h1>
        </div>
        <Link to="/client/new" data-testid="client-new-order-cta" className="lc-btn-primary inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Nouvelle demande
        </Link>
      </div>

      {loading && <div className="text-sm text-[var(--lc-gray)]">Chargement…</div>}
      {!loading && orders.length === 0 && (
        <div className="lc-card p-10 text-center" data-testid="no-orders">
          <div className="text-base font-semibold mb-1">Aucune commande pour l'instant</div>
          <p className="text-sm text-[var(--lc-gray)]">Lancez votre première demande de calcul de prix.</p>
        </div>
      )}

      <div className="grid gap-3" data-testid="orders-list">
        {orders.map((o) => (
          <Link
            to={`/client/orders/${o.id}`}
            key={o.id}
            data-testid={`order-row-${o.id}`}
            className="lc-card p-4 sm:p-5 grid sm:grid-cols-[1fr,auto] gap-4 hover:border-[var(--lc-orange)]/40 transition-colors"
          >
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="font-mono text-xs text-[var(--lc-gray)]">{o.ref}</span>
                <StatusBadge status={o.status} />
              </div>
              <div className="font-semibold text-sm flex items-start gap-1.5">
                <MapPin className="w-3.5 h-3.5 mt-0.5 text-[var(--lc-orange)] shrink-0" />
                {o.arrival_address}
              </div>
              <div className="text-xs text-[var(--lc-gray)] mt-1 flex items-center gap-3">
                <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {fmtDate(o.delivery_date_confirmed || o.delivery_date_wanted)}</span>
                <span>{o.distance_km} km</span>
                <span>{o.package_name || "—"}</span>
              </div>
            </div>
            <div className="flex sm:flex-col sm:items-end justify-between gap-2">
              <div className="text-right">
                {!privacy && (
                  <>
                    <div className="text-xl font-black text-[var(--lc-orange)]">{o.on_quote ? "Sur devis" : fmtPrice(o.total_ttc)}</div>
                    {!o.on_quote && <div className="text-[10px] text-[var(--lc-gray)]">{fmtPrice(o.total_ht)} HT</div>}
                  </>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-[var(--lc-gray)]" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
